import React, { ReactNode, ErrorInfo } from 'react';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryClass extends React.Component<ErrorBoundaryProps & { t: any }, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps & { t: any }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--base)', padding: '24px' }}>
          <div className="card" style={{ maxWidth: 500, padding: '32px 28px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, background: 'rgba(220, 38, 38, 0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span style={{ fontSize: 24 }}>⚠️</span>
            </div>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
              {t('errorBoundary.title')}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.6 }}>
              {t('errorBoundary.message')}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="btn btn-signal"
              style={{ width: '100%' }}
            >
              {t('errorBoundary.returnHome')}
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{ marginTop: 16, textAlign: 'left', color: 'var(--text-secondary)', fontSize: 11 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 8 }}>{t('errorBoundary.details')}</summary>
                <pre style={{ background: 'var(--surface-1)', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 200 }}>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const { t } = useTranslation();
  return <ErrorBoundaryClass t={t}>{children}</ErrorBoundaryClass>;
};
